import { Dispatch, SetStateAction, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";

import { fetchAuthRegisterSubmit, fetchAuthRegisterWith2FA, SetSnackType, timeout } from "../../utils";
import { Password, validatePassword } from ".";

export const DialogRegister = (props: {
  open: boolean;
  registered: (username: string, password: string) => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setSnack: SetSnackType;
}) => {
  const [un, setUn] = useState("");
  const [unErr, setUnErr] = useState(false);
  const [pw, setPw] = useState("");
  const [pwErr, setPwErr] = useState(false);

  const [registering, setRegistering] = useState(false);
  const [open2FA, setOpen2FA] = useState(false);
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState(false);
  const [registering2FA, setRegistering2FA] = useState(false);

  const handleRegisterSubmit = async () => {
    if (!un) {
      setUnErr(true);
      props.setSnack("Invalid username", "error");
      return;
    }
    setUnErr(false);
    setRegistering(true);

    const { fail, err } = validatePassword(pw);
    if (fail) {
      setRegistering(false);
      return props.setSnack(err, "error");
    }

    try {
      const res = await fetchAuthRegisterSubmit(un ?? "", pw ?? "");
      if (res.error) {
        if (res.error === "User already exists") {
          setRegistering(false);
          return props.setSnack(`The username "${res.username}" already exists`, "warning");
        }
        setUnErr(true);
        props.setSnack(res.error, "error");
        return;
      }
    } catch (err: any) {
      setRegistering(false);
      return props.setSnack(err.toString, "error");
    }
    setOpen2FA(true);
  };

  const handleRegisterWith2FA = async () => {
    if (!code) {
      setCodeErr(true);
      return props.setSnack("Check your email for the 2FA code", "error");
    }
    setRegistering2FA(true);

    try {
      const res = await fetchAuthRegisterWith2FA(un, pw, code);
      if (res.error) {
        if (res.error === "User already exists") {
          setRegistering2FA(false);
          return props.setSnack(`The username "${res.username}" is already registered`, "warning");
        }
        setCodeErr(true);
        setRegistering2FA(false);
        return props.setSnack(res.error, "error");
      }
    } catch (err: any) {
      setRegistering2FA(false);
      return props.setSnack(err.toString, "error");
    }
    props.registered(un, pw);
    setUn("");
    setPw("");
    setCode("");
    setRegistering2FA(false);
    setRegistering(false);
    setOpen2FA(false);
    props.setOpen(false);
  };

  return (
    <>
      <Dialog open={props.open} disableRestoreFocus>
        <DialogTitle>Register A New Account</DialogTitle>
        <DialogContent>
          <DialogContentText>Create a new account by entering your username and password here:</DialogContentText>
          <form>
            <TextField
              autoComplete="off"
              autoFocus
              error={unErr}
              fullWidth
              label="Username"
              required
              type="text"
              onChange={ev => {
                setUnErr(false);
                setPwErr(false);
                setUn(ev.target.value ?? "");
              }}
              onKeyDownCapture={ev => {
                if (ev.key === "Enter") handleRegisterSubmit();
              }}
              sx={{ mt: 2 }}
              value={un}
            />
            <Password
              withReqs
              {...{ pw, pwErr, setPw, setPwErr, setSnack: props.setSnack, submit: handleRegisterSubmit }}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={registering}
            onClick={() => {
              setUn("");
              setPw("");
              setRegistering(false);
              props.setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={!un || !pw || registering}
            loading={registering}
            onClick={handleRegisterSubmit}
            variant="outlined"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={open2FA} disableRestoreFocus>
        <DialogTitle>New Account Registration: 2FA</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Check your email for the two-factor authentication code, and enter it here to confirm creating your new
            account:
          </DialogContentText>
          <TextField
            autoFocus
            error={codeErr}
            fullWidth
            label="2FA Code"
            required
            type="text"
            onChange={ev => {
              setCodeErr(false);
              setCode(ev.target.value ?? "");
            }}
            onKeyDownCapture={ev => {
              if (ev.key === "Enter") handleRegisterWith2FA();
            }}
            sx={{ mt: 2 }}
            value={code}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCode("");
              setRegistering2FA(false);
              setRegistering(false);
              setOpen2FA(false);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button disabled={!code} loading={registering2FA} onClick={handleRegisterWith2FA} variant="contained">
            Register
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
